import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAutoLookupCountryWeights,
  useCountryWeights,
  useDeleteCountryWeights,
  useSetCountryWeights,
} from "../../hooks/investments";
import type { CountryWeight, CountryWeightSource } from "../../lib/investments";

export function CountryWeightsSection({
  investmentId,
  kind,
}: {
  investmentId: string;
  kind: string;
}) {
  const { t } = useTranslation();
  const query = useCountryWeights(investmentId);
  const setMut = useSetCountryWeights(investmentId);
  const lookupMut = useAutoLookupCountryWeights(investmentId);
  const delMut = useDeleteCountryWeights(investmentId);

  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<CountryWeight[]>([]);
  const [error, setError] = useState<string | null>(null);

  const data = query.data;

  function startEdit() {
    setError(null);
    setRows(data?.weights ?? []);
    setEditing(true);
  }

  function addRow() {
    setRows((r) => [...r, { country: "", weightBp: 0 }]);
  }

  function updateRow(idx: number, patch: Partial<CountryWeight>) {
    setRows((r) => r.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  }

  function removeRow(idx: number) {
    setRows((r) => r.filter((_, i) => i !== idx));
  }

  function distributeEvenly() {
    if (rows.length === 0) return;
    const per = Math.floor(10_000 / rows.length);
    const rest = 10_000 - per * rows.length;
    setRows((r) =>
      r.map((x, i) => ({ ...x, weightBp: per + (i === 0 ? rest : 0) })),
    );
  }

  async function handleSave() {
    setError(null);
    const cleaned = rows
      .map((r) => ({
        country: r.country.trim().toUpperCase(),
        weightBp: Math.round(r.weightBp),
      }))
      .filter((r) => r.country !== "" && r.weightBp > 0);
    const sum = cleaned.reduce((s, r) => s + r.weightBp, 0);
    if (cleaned.length === 0) {
      setError(
        t("country.error_empty", {
          defaultValue: "Mindestens eine Zeile mit Land + Gewicht erforderlich",
        }) ?? "",
      );
      return;
    }
    if (Math.abs(sum - 10_000) > 5) {
      setError(
        t("country.error_sum", {
          defaultValue: "Summe muss 100 % ergeben (aktuell {{pct}} %)",
          pct: (sum / 100).toFixed(2),
        }) ?? "",
      );
      return;
    }
    try {
      await setMut.mutateAsync(cleaned);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleAutoLookup() {
    setError(null);
    try {
      const r = await lookupMut.mutateAsync();
      if (!r.matched) {
        setError(
          t("country.lookup_no_match", {
            defaultValue:
              "Kein Auto-Treffer. Trage die Gewichte manuell ein.",
          }) ?? "",
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleClear() {
    if (!window.confirm(t("country.confirm_clear", { defaultValue: "Daten wirklich loeschen?" }))) {
      return;
    }
    await delMut.mutateAsync();
  }

  if (!data?.supported && kind !== "ETF" && kind !== "AKTIE") {
    return null;
  }

  return (
    <div className="fv-card overflow-hidden">
      <div
        className="px-5 py-3 flex items-center justify-between border-b"
        style={{ borderColor: "var(--border)" }}
      >
        <div>
          <h2 className="text-base font-semibold">
            {t("country.section_title", { defaultValue: "Länderverteilung" })}
          </h2>
          <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
            {data?.source ? (
              <SourceLabel source={data.source} />
            ) : (
              t("country.section_subtitle", {
                defaultValue: "Noch keine Daten — Auto-Lookup oder manuell anlegen",
              })
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!editing && (
            <>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border text-xs font-medium"
                style={{ borderColor: "var(--border)", background: "var(--card)" }}
                onClick={handleAutoLookup}
                disabled={lookupMut.isPending}
              >
                {lookupMut.isPending
                  ? "…"
                  : t("country.auto_lookup", { defaultValue: "Auto-Lookup" })}
              </button>
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg border text-xs font-medium"
                style={{ borderColor: "var(--border)", background: "var(--card)" }}
                onClick={startEdit}
              >
                {data?.weights
                  ? t("country.edit", { defaultValue: "Bearbeiten" })
                  : t("country.add", { defaultValue: "Manuell anlegen" })}
              </button>
              {data?.weights && (
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg border text-xs font-medium"
                  style={{ borderColor: "var(--border)", background: "var(--card)", color: "var(--neg)" }}
                  onClick={handleClear}
                  disabled={delMut.isPending}
                >
                  {t("country.clear", { defaultValue: "Löschen" })}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="p-5">
        {editing ? (
          <EditTable
            rows={rows}
            onAdd={addRow}
            onUpdate={updateRow}
            onRemove={removeRow}
            onDistribute={distributeEvenly}
            onSave={handleSave}
            onCancel={() => setEditing(false)}
            saving={setMut.isPending}
          />
        ) : data?.weights && data.weights.length > 0 ? (
          <ViewTable weights={data.weights} />
        ) : (
          <div
            className="rounded-lg p-4 text-sm text-center"
            style={{ background: "var(--border-soft)", color: "var(--muted)" }}
          >
            {t("country.empty_hint", {
              defaultValue:
                "Klick auf Auto-Lookup für ETFs/Aktien, oder manuell anlegen.",
            })}
          </div>
        )}
        {error && (
          <div
            className="mt-3 text-xs px-3 py-2 rounded-lg"
            style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function SourceLabel({ source }: { source: CountryWeightSource }) {
  const { t } = useTranslation();
  const label =
    source === "MANUAL"
      ? t("country.source_manual", { defaultValue: "Manuell gepflegt" })
      : source === "ISIN"
        ? t("country.source_isin", { defaultValue: "Aus ISIN abgeleitet" })
        : t("country.source_seed", { defaultValue: "Look-Through-Seed" });
  return <span>{label}</span>;
}

function ViewTable({ weights }: { weights: CountryWeight[] }) {
  const sorted = [...weights].sort((a, b) => b.weightBp - a.weightBp);
  return (
    <table className="w-full text-sm">
      <thead>
        <tr
          className="text-left text-xs"
          style={{ background: "var(--border-soft)", color: "var(--muted)" }}
        >
          <th className="px-3 py-2 font-semibold">Land</th>
          <th className="px-3 py-2 text-right font-semibold">Gewicht</th>
          <th className="px-3 py-2" style={{ width: "50%" }} />
        </tr>
      </thead>
      <tbody>
        {sorted.map((w) => {
          const pct = w.weightBp / 100;
          return (
            <tr
              key={w.country}
              className="border-b"
              style={{ borderColor: "var(--border-soft)" }}
            >
              <td className="px-3 py-2 mono">{w.country}</td>
              <td className="px-3 py-2 text-right num">{pct.toFixed(2)} %</td>
              <td className="px-3 py-2">
                <div
                  className="h-1.5 rounded-full overflow-hidden"
                  style={{ background: "var(--border-soft)" }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, pct).toFixed(2)}%`,
                      height: "100%",
                      background: "var(--accent)",
                    }}
                  />
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function EditTable({
  rows,
  onAdd,
  onUpdate,
  onRemove,
  onDistribute,
  onSave,
  onCancel,
  saving,
}: {
  rows: CountryWeight[];
  onAdd: () => void;
  onUpdate: (idx: number, patch: Partial<CountryWeight>) => void;
  onRemove: (idx: number) => void;
  onDistribute: () => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const sum = rows.reduce((s, r) => s + r.weightBp, 0);
  return (
    <div className="space-y-3">
      <table className="w-full text-sm">
        <thead>
          <tr
            className="text-left text-xs"
            style={{ background: "var(--border-soft)", color: "var(--muted)" }}
          >
            <th className="px-3 py-2 font-semibold w-24">Land (ISO-2)</th>
            <th className="px-3 py-2 font-semibold w-32 text-right">Gewicht %</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr
              key={`row-${idx}`}
              className="border-b"
              style={{ borderColor: "var(--border-soft)" }}
            >
              <td className="px-3 py-2">
                <input
                  type="text"
                  maxLength={2}
                  value={r.country}
                  onChange={(e) =>
                    onUpdate(idx, { country: e.target.value.toUpperCase() })
                  }
                  className="w-16 px-2 py-1 rounded-md text-sm mono"
                  style={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    color: "var(--text)",
                  }}
                  placeholder="DE"
                />
              </td>
              <td className="px-3 py-2 text-right">
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={(r.weightBp / 100).toFixed(2)}
                  onChange={(e) =>
                    onUpdate(idx, {
                      weightBp: Math.round(Number(e.target.value) * 100),
                    })
                  }
                  className="w-24 px-2 py-1 rounded-md text-sm num text-right"
                  style={{
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    color: "var(--text)",
                  }}
                />
              </td>
              <td className="px-3 py-2">
                <button
                  type="button"
                  className="text-xs"
                  style={{ color: "var(--neg)" }}
                  onClick={() => onRemove(idx)}
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div
        className="px-3 py-2 rounded-lg flex items-center justify-between text-xs"
        style={{ background: "var(--border-soft)", color: "var(--muted)" }}
      >
        <span>
          {t("country.sum_label", { defaultValue: "Summe" })}:{" "}
          <b
            className="num"
            style={{
              color:
                Math.abs(sum - 10_000) > 5
                  ? "var(--warn)"
                  : "var(--pos)",
            }}
          >
            {(sum / 100).toFixed(2)} %
          </b>
        </span>
        <div className="flex gap-3">
          <button
            type="button"
            className="text-xs font-medium"
            style={{ color: "var(--accent)" }}
            onClick={onAdd}
          >
            + {t("country.add_row", { defaultValue: "Zeile" })}
          </button>
          {rows.length > 0 && (
            <button
              type="button"
              className="text-xs font-medium"
              style={{ color: "var(--accent)" }}
              onClick={onDistribute}
            >
              {t("country.distribute_evenly", { defaultValue: "Gleich verteilen" })}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          className="px-3 py-2 rounded-lg border text-sm font-medium"
          style={{ borderColor: "var(--border)", background: "var(--card)" }}
          onClick={onCancel}
        >
          {t("common.cancel", { defaultValue: "Abbrechen" })}
        </button>
        <button
          type="button"
          className="px-3 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
          style={{ background: "var(--accent)" }}
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "…" : t("common.save", { defaultValue: "Speichern" })}
        </button>
      </div>
    </div>
  );
}
